import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ELEMENT_CATALOG } from './icons';
import { layerOfItem, type FloorPlanLayer } from './layers';
import type { DimensionView } from './dimensions';
import type { DimensionPreview, ToolMode } from './dimension-tool';
import { createMetrics } from './metrics';
import type { BgTransform, Line, PlacedItem, Transform } from './types';

export type { BgTransform, PlacedItem, Transform } from './types';

export interface FloorPlanCanvasHandle {
  zoomBy: (delta: number) => void;
  resetZoom: () => void;
  getScale: () => number;
  getPlacementMetrics: () => { width: number; height: number } | null;
}

interface FloorPlanCanvasProps {
  selected: string | null;
  iconSize: number;
  gridEnabled: boolean;
  gridSize: number;
  items: PlacedItem[];
  onItemsChange: (items: PlacedItem[]) => void;
  bgImage: HTMLImageElement | null;
  bgTransform: BgTransform;
  positioningPlan: boolean;
  workspaceLocked: boolean;
  onPlanPositionChange: (bgTransform: BgTransform) => void;
  transform: Transform;
  onTransformChange: (t: Transform) => void;
  lines: Line[];
  activeLineId: string;
  highlightedLines: Set<string>;
  hiddenLayers: Set<FloorPlanLayer>;
  activeItemId: string | null;
  onActivateItem: (itemId: string | null) => void;
  onToggleHighlight: (lineId: string) => void;
  onRotateActiveItem: () => void;
  onStartDimensionMode: () => void;
  canStartDimensionMode: boolean;
  onConfigureMaster: (itemId: string) => void;
  onConfigureDoubleSwitch?: (itemId: string) => void;
  toolMode: ToolMode;
  dimensionPreview: DimensionPreview | null;
  dimensions: DimensionView[];
  onDimensionPointerDown: (point: { x: number; y: number }, snapThresholdSourcePx: number) => void;
  onDimensionPointerMove: (point: { x: number; y: number }, snapThresholdSourcePx: number) => void;
  onDimensionPointerUp: (point: { x: number; y: number }, snapThresholdSourcePx: number) => void;
  onDimensionOffsetDragStart: (dimensionId: string) => void;
  onDimensionOffsetChange: (dimensionId: string, offsetPx: number) => void;
  onDimensionOffsetDragEnd: () => void;
  onDimensionContextMenu: (dimensionId: string, clientX: number, clientY: number) => void;
}

interface Viewport {
  width: number;
  height: number;
}

interface DragState {
  id: string;
  startX: number;
  startY: number;
  didMove: boolean;
}

interface DragPreview {
  id: string;
  u: number;
  v: number;
  blockId: string | null;
  deltaU: number;
  deltaV: number;
}

interface PlanDragState {
  startWorldX: number;
  startWorldY: number;
  startBgTransform: BgTransform;
}

interface DimensionOffsetDragState {
  id: string;
}

interface BackgroundBaseRect {
  imageSrc: string;
  naturalWidth: number;
  naturalHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface WorldRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MIN_SCALE = 0.15;
const MAX_SCALE = 8;
const ZOOM_SPEED = 0.001;
const DEFAULT_LINE_ID = 'L1';
const MASTER_SWITCH_TYPE = 'master_switch';
const DOUBLE_SWITCH_TYPE = 'switch_double';
const MASTER_SWITCH_COLOR = '#8b9dc3';
const MASTER_SWITCH_ACTIVE_COLOR = '#f59e0b';
const SNAP_THRESHOLD_SCREEN_PX = 18;
const DIMENSION_HIT_THRESHOLD_SCREEN_PX = 8;
const GROUP_COLORS: Record<string, string> = {
  light: '#f59e0b',
  switch: '#8b9dc3',
  socket: '#06b6d4',
  appliance: '#10b981',
};

function isLineControlledItemType(type: string): boolean {
  const group = ELEMENT_CATALOG[type]?.group;
  return group === 'light' || type === 'switch';
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.tagName === 'BUTTON' ||
    target.isContentEditable
  );
}

function snap(v: number, grid: number): number {
  return Math.round(v / grid) * grid;
}

function snapIfEnabled(v: number, grid: number, enabled: boolean): number {
  return enabled ? snap(v, grid) : v;
}

function toWorld(screenX: number, screenY: number, t: Transform) {
  return {
    x: (screenX - t.offsetX) / t.scale,
    y: (screenY - t.offsetY) / t.scale,
  };
}

function getSvgPoint(svg: SVGSVGElement, clientX: number, clientY: number) {
  const rect = svg.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
}

function createBackgroundBaseRect(
  bgImage: HTMLImageElement | null,
  viewport: Viewport,
  transform: Transform,
) {
  if (!bgImage || bgImage.naturalWidth === 0 || bgImage.naturalHeight === 0) return null;
  const s = Math.min(
    viewport.width / transform.scale / bgImage.naturalWidth,
    viewport.height / transform.scale / bgImage.naturalHeight,
  );
  const width = bgImage.naturalWidth * s;
  const height = bgImage.naturalHeight * s;

  return {
    imageSrc: bgImage.currentSrc || bgImage.src,
    naturalWidth: bgImage.naturalWidth,
    naturalHeight: bgImage.naturalHeight,
    x: (viewport.width / transform.scale - width) / 2,
    y: (viewport.height / transform.scale - height) / 2,
    width,
    height,
  };
}

function getBackgroundRect(baseRect: BackgroundBaseRect | null, bgTransform: BgTransform) {
  if (!baseRect) return null;
  const width = baseRect.width * bgTransform.scale;
  const height = baseRect.height * bgTransform.scale;

  return {
    x: baseRect.x + (baseRect.width - width) / 2 + bgTransform.x,
    y: baseRect.y + (baseRect.height - height) / 2 + bgTransform.y,
    width,
    height,
  };
}

function getViewportWorldRect(viewport: Viewport, transform: Transform): WorldRect {
  return {
    x: -transform.offsetX / transform.scale,
    y: -transform.offsetY / transform.scale,
    width: viewport.width / transform.scale,
    height: viewport.height / transform.scale,
  };
}

function worldToPlanLocal(world: { x: number; y: number }, rect: WorldRect) {
  return {
    u: rect.width === 0 ? 0 : (world.x - rect.x) / rect.width,
    v: rect.height === 0 ? 0 : (world.y - rect.y) / rect.height,
  };
}

function planLocalToWorld(item: PlacedItem, rect: WorldRect) {
  return {
    x: rect.x + item.u * rect.width,
    y: rect.y + item.v * rect.height,
  };
}

function sourceUnitsPerScreenPx(rect: WorldRect, naturalWidth: number, transform: Transform): number {
  if (rect.width === 0 || transform.scale === 0) return 1;
  return naturalWidth / rect.width / transform.scale;
}

function dimensionNormal(a: { x: number; y: number }, b: { x: number; y: number }) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return null;
  return {
    nx: -dy / length,
    ny: dx / length,
    length,
  };
}

function pointDistanceToSegment(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function offsetForPoint(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const normal = dimensionNormal(a, b);
  if (!normal) return 0;
  return (p.x - a.x) * normal.nx + (p.y - a.y) * normal.ny;
}

function readableDimensionAngle(angle: number): { angle: number; flipped: boolean } {
  return angle > 90 || angle < -90
    ? { angle: angle + 180, flipped: true }
    : { angle, flipped: false };
}

function getBlockGroupId(item: PlacedItem): string | null {
  return item.blockId?.trim() || null;
}

function moveBlockGroupItems(items: PlacedItem[], draggedItem: PlacedItem, nextU: number, nextV: number): PlacedItem[] {
  const blockId = getBlockGroupId(draggedItem);
  if (!blockId) {
    return items.map(item => item.id === draggedItem.id ? { ...item, u: nextU, v: nextV } : item);
  }

  const deltaU = nextU - draggedItem.u;
  const deltaV = nextV - draggedItem.v;
  return items.map(item => getBlockGroupId(item) === blockId
    ? { ...item, u: item.u + deltaU, v: item.v + deltaV }
    : item,
  );
}

function deleteBlockGroupItems(items: PlacedItem[], targetItem: PlacedItem): PlacedItem[] {
  const blockId = getBlockGroupId(targetItem);
  return blockId
    ? items.filter(item => getBlockGroupId(item) !== blockId)
    : items.filter(item => item.id !== targetItem.id);
}

function range(start: number, end: number, step: number) {
  const values: number[] = [];
  if (step <= 0) return values;
  for (let value = start; value < end; value += step) values.push(value);
  return values;
}

function useVisibleGrid(viewport: Viewport, transform: Transform, gridSize: number) {
  return useMemo(() => {
    const worldLeft = -transform.offsetX / transform.scale;
    const worldTop = -transform.offsetY / transform.scale;
    const worldRight = (viewport.width - transform.offsetX) / transform.scale;
    const worldBottom = (viewport.height - transform.offsetY) / transform.scale;

    const startX = Math.floor(worldLeft / gridSize) * gridSize;
    const startY = Math.floor(worldTop / gridSize) * gridSize;
    const majorGrid = gridSize * 5;
    const majorStartX = Math.floor(worldLeft / majorGrid) * majorGrid;
    const majorStartY = Math.floor(worldTop / majorGrid) * majorGrid;

    return {
      worldLeft,
      worldTop,
      worldRight,
      worldBottom,
      minorX: range(startX, worldRight, gridSize),
      minorY: range(startY, worldBottom, gridSize),
      majorX: range(majorStartX, worldRight, majorGrid),
      majorY: range(majorStartY, worldBottom, majorGrid),
    };
  }, [gridSize, transform, viewport]);
}

export const FloorPlanCanvas = forwardRef<FloorPlanCanvasHandle, FloorPlanCanvasProps>(
  ({
    selected,
    iconSize,
    gridEnabled,
    gridSize,
    items,
    onItemsChange,
    bgImage,
    bgTransform,
    positioningPlan,
    workspaceLocked,
    onPlanPositionChange,
    transform,
    onTransformChange,
    lines,
    activeLineId,
    highlightedLines,
    hiddenLayers,
    activeItemId,
    onActivateItem,
    onToggleHighlight,
    onRotateActiveItem,
    onStartDimensionMode,
    canStartDimensionMode,
    onConfigureMaster,
    onConfigureDoubleSwitch,
    toolMode,
    dimensionPreview,
    dimensions,
    onDimensionPointerDown,
    onDimensionPointerMove,
    onDimensionPointerUp,
    onDimensionOffsetDragStart,
    onDimensionOffsetChange,
    onDimensionOffsetDragEnd,
    onDimensionContextMenu,
  }, ref) => {
    const { t } = useTranslation();
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const isPanning = useRef(false);
    const didPan = useRef(false);
    const didDragItem = useRef(false);
    const didDragDimensionOffset = useRef(false);
    const panStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
    const spaceHeld = useRef(false);
    const itemDrag = useRef<DragState | null>(null);
    const planDrag = useRef<PlanDragState | null>(null);
    const dimensionDrag = useRef(false);
    const dimensionOffsetDrag = useRef<DimensionOffsetDragState | null>(null);
    const transformRef = useRef(transform);
    const itemsRef = useRef(items);
    const bgTransformRef = useRef(bgTransform);
    const [viewport, setViewport] = useState<Viewport>({ width: 1, height: 1 });
    const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
    const [backgroundBaseRect, setBackgroundBaseRect] = useState<BackgroundBaseRect | null>(null);
    const [emptyCanvasRect, setEmptyCanvasRect] = useState<WorldRect | null>(null);
    const [hoveredDimensionId, setHoveredDimensionId] = useState<string | null>(null);
    const viewportWidth = viewport.width;
    const viewportHeight = viewport.height;

    useEffect(() => { transformRef.current = transform; }, [transform]);
    useEffect(() => { itemsRef.current = items; }, [items]);
    useEffect(() => { bgTransformRef.current = bgTransform; }, [bgTransform]);

    useEffect(() => {
      if (emptyCanvasRect || viewportWidth <= 1 || viewportHeight <= 1) return;
      setEmptyCanvasRect({ x: 0, y: 0, width: viewportWidth, height: viewportHeight });
    }, [emptyCanvasRect, viewportHeight, viewportWidth]);

    useEffect(() => {
      if (!bgImage) {
        setBackgroundBaseRect(null);
        return;
      }
      if (viewportWidth <= 1 || viewportHeight <= 1) return;

      setBackgroundBaseRect(current => {
        if (
          current &&
          current.imageSrc === (bgImage.currentSrc || bgImage.src) &&
          current.naturalWidth === bgImage.naturalWidth &&
          current.naturalHeight === bgImage.naturalHeight
        ) {
          return current;
        }
        return createBackgroundBaseRect(bgImage, { width: viewportWidth, height: viewportHeight }, transform);
      });
    }, [bgImage, transform, viewportHeight, viewportWidth]);

    const backgroundRect = getBackgroundRect(backgroundBaseRect, bgTransform);
    const backgroundMetrics = useMemo(
      () => backgroundBaseRect
        ? createMetrics({ naturalWidth: backgroundBaseRect.naturalWidth, naturalHeight: backgroundBaseRect.naturalHeight })
        : null,
      [backgroundBaseRect],
    );
    const activeDimensionPreview = useMemo(() => {
      if (!dimensionPreview || !backgroundRect || !backgroundMetrics) return null;
      return {
        ...dimensionPreview,
        a: backgroundMetrics.sourceToWorld(dimensionPreview.a, backgroundRect),
        b: backgroundMetrics.sourceToWorld(dimensionPreview.b, backgroundRect),
      };
    }, [backgroundMetrics, backgroundRect, dimensionPreview]);
    const renderedDimensions = useMemo(() => {
      if (!backgroundRect || !backgroundMetrics) return [];
      return dimensions.flatMap(dimension => {
        if (!dimension.a || !dimension.b || !dimension.label) return [];
        const anchoredItems = [dimension.aItemId, dimension.bItemId]
          .flatMap(itemId => {
            if (!itemId) return [];
            const item = items.find(candidate => candidate.id === itemId);
            return item ? [item] : [];
          });
        if (anchoredItems.some(item => hiddenLayers.has(layerOfItem(item)))) return [];
        const normal = dimensionNormal(dimension.a, dimension.b);
        if (!normal) return [];
        const sourceA = dimension.a;
        const sourceB = dimension.b;
        const sourceAo = {
          x: sourceA.x + normal.nx * dimension.offsetPx,
          y: sourceA.y + normal.ny * dimension.offsetPx,
        };
        const sourceBo = {
          x: sourceB.x + normal.nx * dimension.offsetPx,
          y: sourceB.y + normal.ny * dimension.offsetPx,
        };
        const sourceMid = {
          x: (sourceAo.x + sourceBo.x) / 2,
          y: (sourceAo.y + sourceBo.y) / 2,
        };
        const a = backgroundMetrics.sourceToWorld(sourceA, backgroundRect);
        const b = backgroundMetrics.sourceToWorld(sourceB, backgroundRect);
        const ao = backgroundMetrics.sourceToWorld(sourceAo, backgroundRect);
        const bo = backgroundMetrics.sourceToWorld(sourceBo, backgroundRect);
        const mid = backgroundMetrics.sourceToWorld(sourceMid, backgroundRect);
        const angle = Math.atan2(bo.y - ao.y, bo.x - ao.x) * 180 / Math.PI;
        return [{
          id: dimension.id,
          label: dimension.label,
          sourceA,
          sourceB,
          sourceAo,
          sourceBo,
          a,
          b,
          ao,
          bo,
          mid,
          angle,
          offsetPx: dimension.offsetPx,
        }];
      });
    }, [backgroundMetrics, backgroundRect, dimensions, hiddenLayers, items]);
    const pointerEventToSource = useCallback((clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg || !backgroundRect || !backgroundMetrics) return null;
      const point = getSvgPoint(svg, clientX, clientY);
      const world = toWorld(point.x, point.y, transformRef.current);
      if (
        world.x < backgroundRect.x ||
        world.x > backgroundRect.x + backgroundRect.width ||
        world.y < backgroundRect.y ||
        world.y > backgroundRect.y + backgroundRect.height
      ) {
        return null;
      }
      return {
        point: backgroundMetrics.worldToSource(world, backgroundRect),
        snapThresholdSourcePx: sourceUnitsPerScreenPx(backgroundRect, backgroundBaseRect?.naturalWidth ?? 1, transformRef.current) * SNAP_THRESHOLD_SCREEN_PX,
      };
    }, [backgroundBaseRect, backgroundMetrics, backgroundRect]);
    const isDimensionToolActive = toolMode === 'calibrate' || toolMode === 'dimension';
    const findDimensionOffsetHit = useCallback((sourcePoint: { x: number; y: number }) => {
      if (!backgroundRect || !backgroundBaseRect) return null;
      const thresholdSourcePx = sourceUnitsPerScreenPx(
        backgroundRect,
        backgroundBaseRect.naturalWidth,
        transformRef.current,
      ) * DIMENSION_HIT_THRESHOLD_SCREEN_PX;
      return renderedDimensions.find(dimension => (
        pointDistanceToSegment(sourcePoint, dimension.sourceAo, dimension.sourceBo) <= thresholdSourcePx
      )) ?? null;
    }, [backgroundBaseRect, backgroundRect, renderedDimensions]);
    const iconGeometry = useMemo(() => {
      // Icons live on the plan layer: scale them with the plan (bgTransform.scale)
      // so the icon-to-plan ratio stays fixed while positioning/scaling the plan.
      // Item positions are already plan-normalized, so sizes and gaps scale together.
      const r = iconSize * bgTransform.scale;
      const icon = Math.round(r * 0.67);
      return {
        r,
        icon,
        pad: (r - icon) / 2,
        halfR: r / 2,
        haloR: r * 0.62,
        rx: Math.round(r * 0.17),
        masterRx: Math.round(r * 0.22),
      };
    }, [iconSize, bgTransform.scale]);

    useImperativeHandle(ref, () => ({
      zoomBy: (delta: number) => {
        const t = transformRef.current;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, t.scale + delta));
        const cx = viewport.width / 2;
        const cy = viewport.height / 2;
        onTransformChange({
          scale: newScale,
          offsetX: cx - (cx - t.offsetX) * (newScale / t.scale),
          offsetY: cy - (cy - t.offsetY) * (newScale / t.scale),
        });
      },
      resetZoom: () => onTransformChange({ scale: 1, offsetX: 0, offsetY: 0 }),
      getScale: () => transformRef.current.scale,
      getPlacementMetrics: () => {
        const rect = backgroundRect ?? emptyCanvasRect ?? getViewportWorldRect(viewport, transformRef.current);
        return rect.width > 0 && rect.height > 0
          ? { width: rect.width, height: rect.height }
          : null;
      },
    }), [backgroundRect, emptyCanvasRect, onTransformChange, viewport]);

    useEffect(() => {
      const wrap = wrapRef.current;
      if (!wrap) return;

      const ro = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        setViewport({
          width: Math.max(1, width),
          height: Math.max(1, height),
        });
      });
      ro.observe(wrap);
      return () => ro.disconnect();
    }, []);

    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return;

      const onKeyDown = (e: KeyboardEvent) => {
        if (isTextEditingTarget(e.target)) return;
        if (e.code === 'Space' && !e.repeat) {
          spaceHeld.current = true;
          svg.style.cursor = 'grab';
          e.preventDefault();
        }
      };
      const onKeyUp = (e: KeyboardEvent) => {
        if (isTextEditingTarget(e.target)) return;
        if (e.code === 'Space') {
          spaceHeld.current = false;
          svg.style.cursor = isDimensionToolActive ? 'crosshair' : selected ? 'crosshair' : 'default';
        }
      };

      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
      };
    }, [isDimensionToolActive, selected]);

    useEffect(() => {
      const onMouseMove = (e: MouseEvent) => {
        if (dimensionDrag.current) {
          const source = pointerEventToSource(e.clientX, e.clientY);
          if (source) onDimensionPointerMove(source.point, source.snapThresholdSourcePx);
          return;
        }

        if (dimensionOffsetDrag.current) {
          const source = pointerEventToSource(e.clientX, e.clientY);
          const dimension = renderedDimensions.find(candidate => candidate.id === dimensionOffsetDrag.current?.id);
          if (source && dimension) {
            didDragDimensionOffset.current = true;
            onDimensionOffsetChange(
              dimension.id,
              offsetForPoint(source.point, dimension.sourceA, dimension.sourceB),
            );
          }
          return;
        }

        if (isPanning.current) {
          const dx = e.clientX - panStart.current.x;
          const dy = e.clientY - panStart.current.y;
          if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didPan.current = true;
          onTransformChange({
            ...transformRef.current,
            offsetX: panStart.current.offsetX + dx,
            offsetY: panStart.current.offsetY + dy,
          });
          return;
        }

        if (planDrag.current && svgRef.current) {
          const point = getSvgPoint(svgRef.current, e.clientX, e.clientY);
          const world = toWorld(point.x, point.y, transformRef.current);
          const dx = world.x - planDrag.current.startWorldX;
          const dy = world.y - planDrag.current.startWorldY;
          onPlanPositionChange(
            {
              ...planDrag.current.startBgTransform,
              x: planDrag.current.startBgTransform.x + dx,
              y: planDrag.current.startBgTransform.y + dy,
            },
          );
          return;
        }

        if (itemDrag.current && svgRef.current) {
          const draggedItem = itemsRef.current.find(item => item.id === itemDrag.current?.id);
          if (!draggedItem) return;

          const point = getSvgPoint(svgRef.current, e.clientX, e.clientY);
          const world = toWorld(point.x, point.y, transformRef.current);
          const placementRect = backgroundRect ?? emptyCanvasRect ?? getViewportWorldRect(viewport, transformRef.current);
          const x = snapIfEnabled(world.x, gridSize, gridEnabled);
          const y = snapIfEnabled(world.y, gridSize, gridEnabled);
          const dx = e.clientX - itemDrag.current.startX;
          const dy = e.clientY - itemDrag.current.startY;
          if (Math.abs(dx) > 2 || Math.abs(dy) > 2) itemDrag.current.didMove = true;
          const nextLocal = worldToPlanLocal({ x, y }, placementRect);
          setDragPreview({
            id: itemDrag.current.id,
            ...nextLocal,
            blockId: getBlockGroupId(draggedItem),
            deltaU: nextLocal.u - draggedItem.u,
            deltaV: nextLocal.v - draggedItem.v,
          });
        }
      };

      const onMouseUp = (e: MouseEvent) => {
        const svg = svgRef.current;

        if (dimensionDrag.current) {
          const source = pointerEventToSource(e.clientX, e.clientY);
          dimensionDrag.current = false;
          if (source) onDimensionPointerUp(source.point, source.snapThresholdSourcePx);
        }

        if (dimensionOffsetDrag.current) {
          dimensionOffsetDrag.current = null;
          onDimensionOffsetDragEnd();
        }

        if (isPanning.current) {
          isPanning.current = false;
          if (svg) svg.style.cursor = spaceHeld.current || positioningPlan ? 'grab' : (selected ? 'crosshair' : 'default');
        }

        if (planDrag.current) {
          planDrag.current = null;
          if (svg) svg.style.cursor = positioningPlan ? 'grab' : (selected ? 'crosshair' : 'default');
        }

        if (itemDrag.current) {
          const drag = itemDrag.current;
          const preview = dragPreview;
          itemDrag.current = null;
          setDragPreview(null);

          if (drag.didMove && preview) {
            didDragItem.current = true;
            const draggedItem = itemsRef.current.find(item => item.id === drag.id);
            if (!draggedItem) return;
            onItemsChange(
              moveBlockGroupItems(itemsRef.current, draggedItem, preview.u, preview.v),
            );
          }
        }
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    }, [
      backgroundRect,
      dragPreview,
      emptyCanvasRect,
      gridEnabled,
      gridSize,
      onDimensionPointerMove,
      onDimensionPointerUp,
      onDimensionOffsetChange,
      onDimensionOffsetDragEnd,
      onItemsChange,
      onPlanPositionChange,
      onTransformChange,
      pointerEventToSource,
      positioningPlan,
      renderedDimensions,
      selected,
      viewport,
    ]);

    useEffect(() => {
      const svg = svgRef.current;
      if (!svg || isPanning.current) return;
      svg.style.cursor = isDimensionToolActive ? 'crosshair' : positioningPlan ? 'grab' : (selected ? 'crosshair' : 'default');
    }, [isDimensionToolActive, positioningPlan, selected]);

    const grid = useVisibleGrid(viewport, transform, gridSize);
    const lineColorMap = useMemo(() => new Map(lines.map(line => [line.id, line.color])), [lines]);
    const defaultLineColor = lineColorMap.get(DEFAULT_LINE_ID) ?? lines[0]?.color ?? '#ef4444';
    const placementRect = backgroundRect ?? emptyCanvasRect ?? getViewportWorldRect(viewport, transform);
    const renderedItems = useMemo(
      () => items.filter(item => !hiddenLayers.has(layerOfItem(item))).map(item => {
        let renderedItem = item;
        if (dragPreview?.id === item.id) {
          renderedItem = { ...item, u: dragPreview.u, v: dragPreview.v };
        } else if (dragPreview?.blockId && getBlockGroupId(item) === dragPreview.blockId) {
          renderedItem = { ...item, u: item.u + dragPreview.deltaU, v: item.v + dragPreview.deltaV };
        }
        return {
          item: renderedItem,
          world: planLocalToWorld(renderedItem, placementRect),
        };
      }),
      [dragPreview, hiddenLayers, items, placementRect],
    );
    const activeRenderedItem = useMemo(
      () => renderedItems.find(({ item }) => item.id === activeItemId) ?? null,
      [activeItemId, renderedItems],
    );

    const handleWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
      e.preventDefault();
      if (workspaceLocked) return;
      const t = transformRef.current;
      const factor = 1 - e.deltaY * ZOOM_SPEED;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, t.scale * factor));
      const point = getSvgPoint(e.currentTarget, e.clientX, e.clientY);
      onTransformChange({
        scale: newScale,
        offsetX: point.x - (point.x - t.offsetX) * (newScale / t.scale),
        offsetY: point.y - (point.y - t.offsetY) * (newScale / t.scale),
      });
    }, [onTransformChange, workspaceLocked]);

    const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
      if (e.button === 0 && !spaceHeld.current && !positioningPlan) {
        const source = pointerEventToSource(e.clientX, e.clientY);
        if (source) {
          const dimensionHit = findDimensionOffsetHit(source.point);
          if (dimensionHit) {
            e.preventDefault();
            didDragDimensionOffset.current = false;
            dimensionOffsetDrag.current = { id: dimensionHit.id };
            onDimensionOffsetDragStart(dimensionHit.id);
            return;
          }
        }
      }

      if (isDimensionToolActive && e.button === 0 && !spaceHeld.current) {
        const source = pointerEventToSource(e.clientX, e.clientY);
        if (!source) return;
        e.preventDefault();
        dimensionDrag.current = true;
        onDimensionPointerDown(source.point, source.snapThresholdSourcePx);
        return;
      }

      if (positioningPlan && bgImage && e.button === 0 && !spaceHeld.current) {
        e.preventDefault();
        const point = getSvgPoint(e.currentTarget, e.clientX, e.clientY);
        const world = toWorld(point.x, point.y, transformRef.current);
        if (
          !backgroundRect ||
          world.x < backgroundRect.x ||
          world.x > backgroundRect.x + backgroundRect.width ||
          world.y < backgroundRect.y ||
          world.y > backgroundRect.y + backgroundRect.height
        ) {
          return;
        }
        planDrag.current = {
          startWorldX: world.x,
          startWorldY: world.y,
          startBgTransform: bgTransformRef.current,
        };
        e.currentTarget.style.cursor = 'grabbing';
        return;
      }

      if (!workspaceLocked && (spaceHeld.current || e.button === 1)) {
        e.preventDefault();
        isPanning.current = true;
        didPan.current = false;
        panStart.current = {
          x: e.clientX,
          y: e.clientY,
          offsetX: transformRef.current.offsetX,
          offsetY: transformRef.current.offsetY,
        };
        e.currentTarget.style.cursor = 'grabbing';
      }
    }, [
      backgroundRect,
      bgImage,
      findDimensionOffsetHit,
      isDimensionToolActive,
      onDimensionOffsetDragStart,
      onDimensionPointerDown,
      pointerEventToSource,
      positioningPlan,
      workspaceLocked,
    ]);

    const handleItemMouseDown = useCallback((id: string, e: React.MouseEvent<SVGGElement>) => {
      if (isDimensionToolActive) return;
      if (positioningPlan) return;
      if (e.button !== 0 || spaceHeld.current) return;
      e.stopPropagation();
      itemDrag.current = {
        id,
        startX: e.clientX,
        startY: e.clientY,
        didMove: false,
      };
    }, [isDimensionToolActive, positioningPlan]);

    const handleItemClick = useCallback((item: PlacedItem, e: React.MouseEvent<SVGGElement>) => {
      e.stopPropagation();
      if (isDimensionToolActive) return;
      if (positioningPlan) return;
      if (itemDrag.current?.didMove || didDragItem.current) {
        didDragItem.current = false;
        return;
      }
      onActivateItem(item.id);
      if (item.type === MASTER_SWITCH_TYPE) {
        onConfigureMaster(item.id);
        return;
      }
      if (item.type === DOUBLE_SWITCH_TYPE) {
        onConfigureDoubleSwitch?.(item.id);
        return;
      }
      if (!isLineControlledItemType(item.type) || !item.lineId) return;
      onToggleHighlight(item.lineId);
    }, [isDimensionToolActive, onActivateItem, onConfigureMaster, onConfigureDoubleSwitch, onToggleHighlight, positioningPlan]);

    const handleClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
      if (positioningPlan) return;
      if (isPanning.current || didPan.current) {
        didPan.current = false;
        return;
      }
      if (didDragItem.current) {
        didDragItem.current = false;
        return;
      }
      if (didDragDimensionOffset.current) {
        didDragDimensionOffset.current = false;
        return;
      }
      if (isDimensionToolActive) return;
      if (!selected) {
        onActivateItem(null);
        return;
      }

      const point = getSvgPoint(e.currentTarget, e.clientX, e.clientY);
      const world = toWorld(point.x, point.y, transformRef.current);
      const x = snapIfEnabled(world.x, gridSize, gridEnabled);
      const y = snapIfEnabled(world.y, gridSize, gridEnabled);
      const planLocal = worldToPlanLocal({ x, y }, placementRect);

      const idx = items.findIndex(
        it => Math.abs(it.u - planLocal.u) < 0.000001 && Math.abs(it.v - planLocal.v) < 0.000001 && it.type === selected,
      );
      if (idx >= 0) {
        const targetItem = items[idx];
        if (!targetItem) return;
        const blockId = getBlockGroupId(targetItem);
        if (activeItemId && (activeItemId === targetItem.id || (blockId && items.some(item => item.id === activeItemId && getBlockGroupId(item) === blockId)))) {
          onActivateItem(null);
        }
        onItemsChange(deleteBlockGroupItems(items, targetItem));
      } else {
        const nextItem: PlacedItem = {
          id: crypto.randomUUID(),
          type: selected,
          u: planLocal.u,
          v: planLocal.v,
          lineId: selected === DOUBLE_SWITCH_TYPE ? null : isLineControlledItemType(selected) ? activeLineId || DEFAULT_LINE_ID : null,
          rotation: 0,
          switchLines: selected === DOUBLE_SWITCH_TYPE ? { left: null, right: null } : undefined,
        };
        onItemsChange([
          ...items,
          nextItem,
        ]);
        onActivateItem(nextItem.id);
      }
    }, [
      activeItemId,
      activeLineId,
      gridEnabled,
      gridSize,
      isDimensionToolActive,
      items,
      onActivateItem,
      onItemsChange,
      placementRect,
      positioningPlan,
      selected,
    ]);

    const handleContextMenu = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      if (positioningPlan) return;
      const source = pointerEventToSource(e.clientX, e.clientY);
      if (source) {
        const dimensionHit = findDimensionOffsetHit(source.point);
        if (dimensionHit) {
          onDimensionContextMenu(dimensionHit.id, e.clientX, e.clientY);
          return;
        }
      }
      if (isDimensionToolActive) return;
      const point = getSvgPoint(e.currentTarget, e.clientX, e.clientY);
      const world = toWorld(point.x, point.y, transformRef.current);
      const threshold = (gridSize / 2) / transformRef.current.scale;
      const idx = renderedItems.findIndex(
        renderedItem => Math.abs(renderedItem.world.x - world.x) < threshold && Math.abs(renderedItem.world.y - world.y) < threshold,
      );
      if (idx >= 0) {
        const targetItem = renderedItems[idx]?.item;
        if (!targetItem) return;
        const blockId = getBlockGroupId(targetItem);
        if (activeItemId && (activeItemId === targetItem.id || (blockId && items.some(item => item.id === activeItemId && getBlockGroupId(item) === blockId)))) {
          onActivateItem(null);
        }
        onItemsChange(deleteBlockGroupItems(items, targetItem));
      }
    }, [
      activeItemId,
      findDimensionOffsetHit,
      gridSize,
      isDimensionToolActive,
      items,
      onActivateItem,
      onDimensionContextMenu,
      onItemsChange,
      pointerEventToSource,
      positioningPlan,
      renderedItems,
    ]);

    return (
      <div ref={wrapRef} className="relative w-full h-full bg-surface">
        <svg
          ref={svgRef}
          className="absolute inset-0 h-full w-full select-none"
          viewBox={`0 0 ${viewport.width} ${viewport.height}`}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          role="img"
          aria-label={t('floor_plan.canvas_aria')}
        >
          <rect width={viewport.width} height={viewport.height} fill="rgb(var(--surface))" />
          <defs>
            <filter id="line-highlight-glow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g transform={`translate(${transform.offsetX} ${transform.offsetY}) scale(${transform.scale})`}>
            {backgroundRect && (
              <>
                <image
                  href={bgImage?.src}
                  x={backgroundRect.x}
                  y={backgroundRect.y}
                  width={backgroundRect.width}
                  height={backgroundRect.height}
                  opacity={0.5}
                  preserveAspectRatio="none"
                />
                {positioningPlan && (
                  <>
                    <rect
                      x={backgroundRect.x}
                      y={backgroundRect.y}
                      width={backgroundRect.width}
                      height={backgroundRect.height}
                      fill="transparent"
                      stroke="rgb(var(--accent))"
                      strokeDasharray={`${10 / transform.scale} ${7 / transform.scale}`}
                      strokeWidth={2 / transform.scale}
                    />
                    <g transform={`translate(${backgroundRect.x + 10 / transform.scale} ${backgroundRect.y + 10 / transform.scale})`}>
                      <rect
                        width={112 / transform.scale}
                        height={24 / transform.scale}
                        rx={6 / transform.scale}
                        fill="rgb(var(--accent))"
                        opacity={0.92}
                      />
                      <text
                        x={10 / transform.scale}
                        y={16 / transform.scale}
                        fill="white"
                        fontSize={11 / transform.scale}
                        fontWeight={600}
                      >
                        {t('floor_plan.position_plan_mode')}
                      </text>
                    </g>
                  </>
                )}
              </>
            )}

            {gridEnabled && (
              <g strokeLinecap="square">
                {grid.minorX.map(x => (
                  <line
                    key={`mx-${x}`}
                    x1={x}
                    y1={grid.worldTop}
                    x2={x}
                    y2={grid.worldBottom}
                    stroke="rgba(59,130,246,0.08)"
                    strokeWidth={0.5 / transform.scale}
                  />
                ))}
                {grid.minorY.map(y => (
                  <line
                    key={`my-${y}`}
                    x1={grid.worldLeft}
                    y1={y}
                    x2={grid.worldRight}
                    y2={y}
                    stroke="rgba(59,130,246,0.08)"
                    strokeWidth={0.5 / transform.scale}
                  />
                ))}
                {grid.majorX.map(x => (
                  <line
                    key={`Mx-${x}`}
                    x1={x}
                    y1={grid.worldTop}
                    x2={x}
                    y2={grid.worldBottom}
                    stroke="rgba(59,130,246,0.18)"
                    strokeWidth={0.8 / transform.scale}
                  />
                ))}
                {grid.majorY.map(y => (
                  <line
                    key={`My-${y}`}
                    x1={grid.worldLeft}
                    y1={y}
                    x2={grid.worldRight}
                    y2={y}
                    stroke="rgba(59,130,246,0.18)"
                    strokeWidth={0.8 / transform.scale}
                  />
                ))}
              </g>
            )}

            {activeDimensionPreview && (
              <g pointerEvents="none">
                <line
                  x1={activeDimensionPreview.a.x}
                  y1={activeDimensionPreview.a.y}
                  x2={activeDimensionPreview.b.x}
                  y2={activeDimensionPreview.b.y}
                  stroke={activeDimensionPreview.mode === 'calibration' ? '#f59e0b' : '#0ea5e9'}
                  strokeWidth={2 / transform.scale}
                  strokeDasharray={`${6 / transform.scale} ${4 / transform.scale}`}
                />
                {[activeDimensionPreview.a, activeDimensionPreview.b].map((point, index) => (
                  <circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r={4 / transform.scale}
                    fill="rgb(var(--surface))"
                    stroke={activeDimensionPreview.mode === 'calibration' ? '#f59e0b' : '#0ea5e9'}
                    strokeWidth={2 / transform.scale}
                  />
                ))}
              </g>
            )}

            {renderedItems.map(({ item, world }) => {
              const catalogItem = ELEMENT_CATALOG[item.type];
              if (!catalogItem) return null;
              const Icon = catalogItem.Icon;
              const isMasterSwitch = item.type === MASTER_SWITCH_TYPE;
              const isLineControlledItem = isLineControlledItemType(item.type);
              const highlightedLineId = isLineControlledItem ? item.lineId : null;
              const color = isMasterSwitch
                ? MASTER_SWITCH_COLOR
                : isLineControlledItem
                  ? lineColorMap.get(item.lineId || DEFAULT_LINE_ID) ?? defaultLineColor
                  : GROUP_COLORS[catalogItem.group] ?? defaultLineColor;
              const isHighlighted = highlightedLineId ? highlightedLines.has(highlightedLineId) : false;
              const isMasterActive = isMasterSwitch && highlightedLines.size > 0;
              const isActiveItem = activeItemId === item.id;
              const rotation = item.rotation ?? 0;
              const iconSizeForItem = iconGeometry.icon + (isHighlighted || isMasterActive ? Math.max(2, Math.round(iconGeometry.r * 0.06)) : 0);

              return (
                <g
                  key={item.id}
                  transform={`translate(${world.x - iconGeometry.halfR} ${world.y - iconGeometry.halfR})`}
                  onMouseDown={e => handleItemMouseDown(item.id, e)}
                  onClick={e => handleItemClick(item, e)}
                  opacity={positioningPlan ? 0.55 : 1}
                  pointerEvents={positioningPlan || isDimensionToolActive ? 'none' : 'auto'}
                  style={{ cursor: positioningPlan || isDimensionToolActive ? 'default' : (spaceHeld.current ? 'grab' : 'move') }}
                  filter={isHighlighted || isMasterActive ? 'url(#line-highlight-glow)' : undefined}
                >
                  <rect width={iconGeometry.r} height={iconGeometry.r} rx={isMasterSwitch ? iconGeometry.masterRx : iconGeometry.rx} fill="transparent" />
                  {isActiveItem && (
                    <rect
                      x={1 / transform.scale}
                      y={1 / transform.scale}
                      width={iconGeometry.r - 2 / transform.scale}
                      height={iconGeometry.r - 2 / transform.scale}
                      rx={isMasterSwitch ? iconGeometry.masterRx : iconGeometry.rx}
                      fill="none"
                      stroke="rgb(var(--accent))"
                      strokeWidth={2 / transform.scale}
                      strokeDasharray={`${4 / transform.scale} ${3 / transform.scale}`}
                    />
                  )}
                  {isHighlighted && (
                    <circle
                      cx={iconGeometry.halfR}
                      cy={iconGeometry.halfR}
                      r={iconGeometry.haloR}
                      fill={color}
                      opacity={0.16}
                      stroke={color}
                      strokeWidth={2.5 / transform.scale}
                    />
                  )}
                  {isMasterActive && (
                    <circle
                      cx={iconGeometry.halfR}
                      cy={iconGeometry.halfR}
                      r={iconGeometry.haloR}
                      fill={MASTER_SWITCH_ACTIVE_COLOR}
                      opacity={0.18}
                      stroke={MASTER_SWITCH_ACTIVE_COLOR}
                      strokeWidth={2.5 / transform.scale}
                    >
                      <animate attributeName="opacity" values="0.08;0.28;0.08" dur="1.2s" repeatCount="indefinite" />
                      <animate attributeName="r" values={`${iconGeometry.haloR - iconGeometry.r * 0.06};${iconGeometry.haloR + iconGeometry.r * 0.06};${iconGeometry.haloR - iconGeometry.r * 0.06}`} dur="1.2s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <g transform={`translate(${iconGeometry.halfR} ${iconGeometry.halfR}) rotate(${rotation}) translate(${-iconSizeForItem / 2} ${-iconSizeForItem / 2})`}>
                    <Icon size={iconSizeForItem} color={isMasterActive ? MASTER_SWITCH_ACTIVE_COLOR : color} />
                  </g>
                </g>
              );
            })}

            {activeRenderedItem && !selected && !positioningPlan && !isDimensionToolActive && (
              <g transform={`translate(${activeRenderedItem.world.x} ${activeRenderedItem.world.y}) scale(${1 / transform.scale})`}>
                <foreignObject
                  x={-54}
                  y={-(iconGeometry.halfR * transform.scale) - 38}
                  width={108}
                  height={32}
                  style={{ overflow: 'visible' }}
                >
                  <div
                    className="flex h-8 w-[108px] items-center gap-1 rounded-md border border-border bg-surface/95 p-1 text-ink shadow-lg backdrop-blur-sm"
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="flex h-6 w-8 items-center justify-center rounded border border-border-2 bg-surface-2 text-sm leading-none text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink"
                      onClick={event => {
                        event.stopPropagation();
                        onRotateActiveItem();
                      }}
                      aria-label={t('floor_plan.rotate_icon')}
                      title={t('floor_plan.rotate_icon')}
                    >
                      ↻
                    </button>
                    <button
                      type="button"
                      className="flex h-6 min-w-0 flex-1 items-center justify-center rounded border border-border-2 bg-surface-2 px-2 text-[11px] font-medium text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink disabled:opacity-50"
                      onClick={event => {
                        event.stopPropagation();
                        if (canStartDimensionMode) onStartDimensionMode();
                      }}
                      disabled={!canStartDimensionMode}
                      aria-label={t('floor_plan.dimension_tool')}
                      title={t('floor_plan.dimension_tool')}
                    >
                      {t('floor_plan.dimension_tool')}
                    </button>
                  </div>
                </foreignObject>
              </g>
            )}

            {renderedDimensions.map(dimension => {
              const tick = 6 / transform.scale;
              const labelOffset = 10 / transform.scale;
              const labelWidth = Math.max(68, dimension.label.length * 6.5 + 18) / transform.scale;
              const labelHeight = 18 / transform.scale;
              const labelAngle = readableDimensionAngle(dimension.angle);
              const labelDirection = (dimension.offsetPx >= 0 ? 1 : -1) * (labelAngle.flipped ? -1 : 1);
              const labelY = labelDirection * (8 / transform.scale + labelOffset);
              const isDimensionHovered = hoveredDimensionId === dimension.id;
              return (
                <g key={dimension.id}>
                  <g pointerEvents="none">
                    <line
                      x1={dimension.a.x}
                      y1={dimension.a.y}
                      x2={dimension.ao.x}
                      y2={dimension.ao.y}
                      stroke="#000"
                      strokeWidth={0.8 / transform.scale}
                      opacity={0.9}
                    />
                    <line
                      x1={dimension.b.x}
                      y1={dimension.b.y}
                      x2={dimension.bo.x}
                      y2={dimension.bo.y}
                      stroke="#000"
                      strokeWidth={0.8 / transform.scale}
                      opacity={0.9}
                    />
                    <line
                      x1={dimension.ao.x}
                      y1={dimension.ao.y}
                      x2={dimension.bo.x}
                      y2={dimension.bo.y}
                      stroke="#000"
                      strokeWidth={0.9 / transform.scale}
                    />
                    <g transform={`translate(${dimension.ao.x} ${dimension.ao.y}) rotate(${dimension.angle + 45})`}>
                      <line x1={-tick} y1={0} x2={tick} y2={0} stroke="#000" strokeWidth={0.9 / transform.scale} />
                    </g>
                    <g transform={`translate(${dimension.bo.x} ${dimension.bo.y}) rotate(${dimension.angle + 45})`}>
                      <line x1={-tick} y1={0} x2={tick} y2={0} stroke="#000" strokeWidth={0.9 / transform.scale} />
                    </g>
                  </g>
                  <g
                    transform={`translate(${dimension.mid.x} ${dimension.mid.y}) rotate(${labelAngle.angle})`}
                    onMouseDown={event => {
                      event.stopPropagation();
                      if (event.button !== 0 || spaceHeld.current || positioningPlan) return;
                      event.preventDefault();
                      didDragDimensionOffset.current = false;
                      dimensionOffsetDrag.current = { id: dimension.id };
                      onDimensionOffsetDragStart(dimension.id);
                    }}
                    onClick={event => {
                      event.stopPropagation();
                      if (didDragDimensionOffset.current) {
                        didDragDimensionOffset.current = false;
                        return;
                      }
                      onDimensionContextMenu(dimension.id, event.clientX, event.clientY);
                    }}
                    onMouseEnter={() => setHoveredDimensionId(dimension.id)}
                    onMouseLeave={() => setHoveredDimensionId(current => current === dimension.id ? null : current)}
                    style={{ cursor: isDimensionHovered ? 'grab' : 'pointer' }}
                  >
                    <rect
                      x={-labelWidth / 2}
                      y={labelY - labelHeight / 2}
                      width={labelWidth}
                      height={labelHeight}
                      rx={4 / transform.scale}
                      fill={isDimensionHovered ? '#e0f2fe' : 'transparent'}
                      stroke={isDimensionHovered ? '#0ea5e9' : 'transparent'}
                      strokeWidth={1.4 / transform.scale}
                      opacity={isDimensionHovered ? 0.96 : 1}
                    />
                    <text
                      x={0}
                      y={labelY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={isDimensionHovered ? '#0369a1' : '#0f172a'}
                      fontSize={11 / transform.scale}
                      fontWeight={600}
                      pointerEvents="none"
                    >
                      {dimension.label}
                    </text>
                  </g>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    );
  },
);

FloorPlanCanvas.displayName = 'FloorPlanCanvas';
